class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Opening is opt-in: only a real add to cart should slide the drawer out.
  // Apps re-render it in the background too — DollarLabs' gift interceptor
  // fetches ?sections=cart-drawer,cart-icon-bubble and calls this after it
  // syncs a gift — and those repaints used to pop the drawer open on load.
  renderContents(parsedState, { open = false } = {}) {
    // On the way to checkout the cart is emptied and refilled (see the note
    // at the bottom of this file). Repaints asked for in that window are held
    // back, so the drawer keeps showing the cart the customer clicked on.
    if (document.documentElement.classList.contains('cart-checking-out')) {
      this.heldRender = { parsedState, open };
      return;
    }
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (typeof window.initDeliveryTimerBadges === 'function') {
      window.initDeliveryTimerBadges();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (open) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', function () {
  // Check for the cart_open query parameter on any page
  const params = new URLSearchParams(window.location.search);
  if (params.get('cart_open') === 'true') {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }
});

// Checkout with a subscription in the cart is not a plain navigation. The
// Firmhouse app swaps each line to its SEPA plan first, which it does by
// clearing the cart and adding every line again. For about a second the cart
// really is empty, and the gift apps (BOGOS, DollarLabs) react to that: they
// repaint the drawer through renderContents and put `is-empty` on it, which
// showed "Je winkelwagen is leeg" and pushed the free-shipping bar down right
// before the checkout opened. Nothing is lost, so from the click until the
// page navigates the drawer is frozen: no repaint, no empty state, nothing
// moves.
(function () {
  const BUSY_CLASS = 'cart-checking-out';
  const EMPTY_CLASS = 'is-empty';
  const root = document.documentElement;
  let timer = null;
  let observer = null;

  const release = () => {
    if (!root.classList.contains(BUSY_CLASS)) return;
    root.classList.remove(BUSY_CLASS);
    window.clearTimeout(timer);
    if (observer) observer.disconnect();
    observer = null;

    // Still on the page, so the checkout did not open (or the customer came
    // back). Catch up with the real cart: paint what was held back, and take
    // the empty state from the cart itself.
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const held = drawer.heldRender;
    drawer.heldRender = null;
    if (held) drawer.renderContents(held.parsedState, { open: false });
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => drawer.classList.toggle(EMPTY_CLASS, cart.item_count === 0))
      .catch(() => {});
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest("button[name='checkout'], [data-fh-button-type]");
      const drawer = button && button.closest('cart-drawer');
      if (!drawer || button.disabled || root.classList.contains(BUSY_CLASS)) return;

      drawer.heldRender = null;
      root.classList.add(BUSY_CLASS);

      // An observer's callback runs before the browser paints, so an
      // `is-empty` set by an app is taken off again without ever showing.
      const wasEmpty = drawer.classList.contains(EMPTY_CLASS);
      observer = new MutationObserver(() => {
        if (drawer.classList.contains(EMPTY_CLASS) !== wasEmpty) {
          drawer.classList.toggle(EMPTY_CLASS, wasEmpty);
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

      // If the checkout never opens (a failed add, a blocked navigation) the
      // drawer goes back to following the cart.
      window.clearTimeout(timer);
      timer = window.setTimeout(release, 15000);
    },
    true
  );

  // Coming back with the browser's back button restores the page as it was.
  window.addEventListener('pageshow', release);
})();
